import type { ChannelSegmentsResp, SegmentResp } from "@/api/diagnostics";

/**
 * action_source 값 구간을 화면 띠로 바꾸는 순수 모델.
 *
 * 원본 구간은 hatchery-data `probe_segments` 가 행 순서대로 자른 run / gap / invalid 다.
 * 그대로 그리면 30~60ms gap 과 1~5행 run 이 에피소드마다 1px 틈과 줄을 수십 개 만든다
 * (창원 실측: 원소 447 → 띠 286). 그래서 **ZOH(zero-order hold)** 로 합친다.
 * 학습(learning-core `demonstration_mask`)이 이 채널을 읽는 방식과 같다.
 *
 * - gap 은 앞 구간에 합친다. 원소의 끝은 다음 원소(gap 제외)의 시작이다. run 의 `t_end` 는
 *   마지막 행 시각이라서 다음 run 과 한 주기만큼 떨어져 있다.
 * - 마지막 원소는 `max(t_end, t_start + 주기)` 까지다. 한 행짜리 꼬리(SCRIPT)가 폭 0 으로
 *   사라지지 않게 한 주기만큼 확보한다.
 * - 이웃한 같은 범주를 잇는다. 정의 밖 값은 같은 code 끼리만 잇는다.
 * - 짧은 run 은 흡수하지 않는다. 실제로 있었던 짧은 개입을 지우지 않는다.
 *
 * Owns: 범주 판정, ZOH 합치기, 범주별 순번·합계, 채널 사실(gap 수·최대 gap), 시각으로 띠 찾기.
 * Does not own: 빈 목록의 뜻(`@/api/diagnosticSegments`), 문구·색(`actionSourceTerms`),
 * c6 타임라인 모양(`segmentTimeline`).
 */

export type ActionSourceCategory =
  | "INFERENCE"
  | "INTERVENTION"
  | "TELEOP"
  | "SCRIPT"
  // 정수지만 `ActionSource` 어휘에 없는 값.
  | "unknown"
  // NaN·비정수 행.
  | "invalid";

const KNOWN_SOURCES: ReadonlySet<string> = new Set([
  "INFERENCE",
  "INTERVENTION",
  "TELEOP",
  "SCRIPT",
]);

/**
 * 바탕 출처인지 판정한다. Policy 가 스스로 낸 INFERENCE 는 에피소드 대부분(창원 90~98%)을
 * 차지한다. 이동과 음영은 이 바탕 밖(사람·스크립트·무효/정의 밖 값)만 표시한다.
 */
export function isBaselineCategory(category: ActionSourceCategory): boolean {
  return category === "INFERENCE";
}

/** 범주 표시 순서. 합계 표와 요약이 이 순서를 따른다. */
export const ACTION_SOURCE_CATEGORY_ORDER: readonly ActionSourceCategory[] = [
  "INTERVENTION",
  "TELEOP",
  "INFERENCE",
  "SCRIPT",
  "unknown",
  "invalid",
];

export type ActionSourceBand = {
  // 첫 원소의 시작 행. 에피소드 안에서 안정적인 키다. 구간별 지표를 붙일 때 이 키로 연결한다.
  id: string;
  category: ActionSourceCategory;
  // 정의 밖 값(unknown)이면 그 정수다. 나머지는 원본 code 그대로다(invalid 는 null).
  code: number | null;
  startSeconds: number;
  endSeconds: number;
  rowStart: number;
  nRows: number;
  // 이 띠로 합친 원본 run 수(gap 제외).
  runCount: number;
  // 같은 범주 안에서 몇 번째 띠인가(1부터)와 그 범주의 띠 수.
  ordinal: number;
  ordinalCount: number;
};

export type ActionSourceTotal = {
  category: ActionSourceCategory;
  seconds: number;
  // 전체 span 대비 몫(0~1).
  share: number;
  bandCount: number;
  nRows: number;
};

export type ActionSourceFacts = {
  gapCount: number;
  // 가장 긴 gap(초). gap 이 없으면 null.
  maxGapSeconds: number | null;
  invalidRows: number;
  unknownRows: number;
  // 시각이 없어 띠로 옮기지 못하고 건너뛴 원소 수.
  skippedElements: number;
};

export type ActionSourceModel = {
  bands: readonly ActionSourceBand[];
  totals: readonly ActionSourceTotal[];
  facts: ActionSourceFacts;
  // 첫 띠 시작 ~ 마지막 띠 끝(초).
  spanSeconds: number;
};

// 주기를 모를 때 한 행 꼬리에 주는 폭. 창원 action_source 는 100 Hz 다.
const FALLBACK_ROW_SECONDS = 0.01;

// 영상과 기록의 양 끝은 몇 프레임 차이가 난다(창원 실측: 카메라 첫 시각 −0.06~+0.02초, mp4
// 40.03초 vs 기록 39.97초). 첫 띠 앞과 마지막 띠 뒤의 이 범위는 그 띠로 본다.
const EDGE_TOLERANCE_SECONDS = 0.1;

type TimedElement = SegmentResp & { t_start: number; t_end: number };

function isTimed(element: SegmentResp): element is TimedElement {
  return (
    element.t_start !== null &&
    element.t_end !== null &&
    Number.isFinite(element.t_start) &&
    Number.isFinite(element.t_end)
  );
}

/**
 * 출처 어휘 하나를 범주로 바꾼다. 어휘에 없으면 `unknown` 이다.
 *
 * 구간 목록(`SegmentResp.source`)과 타임라인 아티팩트(`findings.states` 의 `state`)는 같은
 * 어휘를 쓴다. 그래서 판정도 한 곳에 둔다. 두 소비처가 각자 문자열을 비교하면 어휘가 늘 때
 * 한쪽만 바뀐다.
 */
export function actionSourceCategoryOf(source: string | null): ActionSourceCategory {
  return source !== null && KNOWN_SOURCES.has(source)
    ? (source as ActionSourceCategory)
    : "unknown";
}

function categoryOf(element: SegmentResp): ActionSourceCategory {
  if (element.kind === "invalid") return "invalid";
  return actionSourceCategoryOf(element.source);
}

// 같은 띠로 이을 수 있는지 판정한다. 정의 밖 값은 값이 다르면 다른 띠다.
function sameBand(band: ActionSourceBand, category: ActionSourceCategory, code: number | null) {
  return band.category === category && (category !== "unknown" || band.code === code);
}

export function buildActionSourceModel(channel: ChannelSegmentsResp): ActionSourceModel {
  const known = channel.segments.filter(
    (element) => element.kind === "run" || element.kind === "gap" || element.kind === "invalid",
  );
  const timed = known.filter(isTimed);
  const rowSeconds =
    channel.dt_median_seconds !== null && channel.dt_median_seconds > 0
      ? channel.dt_median_seconds
      : FALLBACK_ROW_SECONDS;

  // 원소마다 뒤따르는 첫 비-gap 원소의 시작. 원소는 수천 개일 수 있어서 뒤에서부터 한 번만
  // 훑어 구한다.
  const nextStart: (number | null)[] = new Array<number | null>(timed.length).fill(null);
  for (let i = timed.length - 2; i >= 0; i -= 1) {
    const following = timed[i + 1];
    nextStart[i] = following.kind === "gap" ? nextStart[i + 1] : following.t_start;
  }

  const bands: ActionSourceBand[] = [];
  let gapCount = 0;
  let maxGapSeconds: number | null = null;
  for (let i = 0; i < timed.length; i += 1) {
    const element = timed[i];
    if (element.kind === "gap") {
      gapCount += 1;
      const length = element.t_end - element.t_start;
      maxGapSeconds = maxGapSeconds === null ? length : Math.max(maxGapSeconds, length);
      continue;
    }
    const endSeconds = nextStart[i] ?? Math.max(element.t_end, element.t_start + rowSeconds);
    const category = categoryOf(element);
    const last = bands[bands.length - 1];
    if (last !== undefined && sameBand(last, category, element.code)) {
      last.endSeconds = endSeconds;
      last.nRows += element.n_rows;
      last.runCount += 1;
      continue;
    }
    bands.push({
      id: String(element.row_start),
      category,
      code: element.code,
      startSeconds: element.t_start,
      endSeconds,
      rowStart: element.row_start,
      nRows: element.n_rows,
      runCount: 1,
      ordinal: 0,
      ordinalCount: 0,
    });
  }

  const counts = new Map<ActionSourceCategory, number>();
  for (const band of bands) {
    band.ordinal = (counts.get(band.category) ?? 0) + 1;
    counts.set(band.category, band.ordinal);
  }
  for (const band of bands) {
    band.ordinalCount = counts.get(band.category) ?? 0;
  }

  const spanSeconds =
    bands.length === 0 ? 0 : bands[bands.length - 1].endSeconds - bands[0].startSeconds;
  const totals = ACTION_SOURCE_CATEGORY_ORDER.flatMap((category) => {
    const members = bands.filter((band) => band.category === category);
    if (members.length === 0) return [];
    const seconds = members.reduce((sum, band) => sum + (band.endSeconds - band.startSeconds), 0);
    return [
      {
        category,
        seconds,
        share: spanSeconds > 0 ? seconds / spanSeconds : 0,
        bandCount: members.length,
        nRows: members.reduce((sum, band) => sum + band.nRows, 0),
      },
    ];
  });

  return {
    bands,
    totals,
    facts: {
      gapCount,
      maxGapSeconds,
      invalidRows: channel.invalid_rows,
      unknownRows: channel.unknown_rows,
      skippedElements: channel.segments.length - timed.length,
    },
    spanSeconds,
  };
}

/**
 * 이 시각(영상 초)을 포함하는 띠의 index. 범위 밖이면 null 이다. 기록이 없는 자리를 가까운
 * 띠로 간주하지 않는다. 단, 양 끝의 몇 프레임은 영상과 기록의 차이로 생긴 자리라서 첫 띠와
 * 마지막 띠로 본다.
 */
export function bandIndexAt(bands: readonly ActionSourceBand[], seconds: number): number | null {
  let low = 0;
  let high = bands.length - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    const band = bands[mid];
    if (seconds < band.startSeconds) {
      high = mid - 1;
    } else if (seconds >= band.endSeconds) {
      low = mid + 1;
    } else {
      return mid;
    }
  }
  const last = bands.length - 1;
  if (last < 0) return null;
  if (seconds < bands[0].startSeconds) {
    return seconds >= bands[0].startSeconds - EDGE_TOLERANCE_SECONDS ? 0 : null;
  }
  if (seconds >= bands[last].endSeconds) {
    return seconds <= bands[last].endSeconds + EDGE_TOLERANCE_SECONDS ? last : null;
  }
  return null;
}
